import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api } from "../api/endpoints";
import { ApiError, getToken, setPasswordRequiredHandler, setToken, setUnauthorizedHandler } from "../api/client";
import type { AuthResponse, Site, User } from "../api/types";
import { forgetPushDevice } from "../lib/push";

// One organization, owner-managed accounts. A person signing in with a
// temporary password lands in "password-required" and sees nothing else until
// they set their own (the server refuses everything else with 403 anyway).

type AuthState =
  | { status: "loading" }
  | { status: "signed-out"; notice?: string }
  | { status: "password-required"; token: string; user: User; site: Site }
  | { status: "signed-in"; token: string; user: User; site: Site }
  | { status: "error"; message: string };

interface AuthApi {
  state: AuthState;
  /** After login or a password change: stores the token and routes by must_change_password. */
  signIn: (res: AuthResponse) => void;
  signOut: (notice?: string) => Promise<void>;
  updateSite: (site: Site) => void;
  updateUser: (user: User) => void;
  retry: () => void;
}

const Ctx = createContext<AuthApi | null>(null);
const BOOT_FLAG = "br.booted";
const SESSION_ENDED = "Your session ended. Sign in again.";

function fromResponse(token: string, user: User, site: Site): AuthState {
  return user.must_change_password ? { status: "password-required", token, user, site } : { status: "signed-in", token, user, site };
}

function clearBootFlag(): void {
  try {
    window.sessionStorage.removeItem(BOOT_FLAG);
  } catch {
    // Storage blocked; nothing to clear.
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(() => (getToken() ? { status: "loading" } : { status: "signed-out" }));
  const [attempt, setAttempt] = useState(0);

  // Validate a stored token on boot (and when the server says a password change is due).
  useEffect(() => {
    const token = getToken();
    if (!token) {
      setState((s) => (s.status === "signed-out" ? s : { status: "signed-out" }));
      return;
    }
    const ctrl = new AbortController();
    api
      .me(ctrl.signal)
      .then((me) => setState(fromResponse(token, me.user, me.site)))
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof ApiError && err.status === 401) {
          setToken(null);
          setState({ status: "signed-out", notice: SESSION_ENDED });
        } else {
          setState({ status: "error", message: err instanceof Error ? err.message : "Could not load your account." });
        }
      });
    return () => ctrl.abort();
  }, [attempt]);

  useEffect(() => {
    // A 401 anywhere means the session was killed (password reset, user removed, expired).
    setUnauthorizedHandler(() => {
      setToken(null);
      clearBootFlag();
      setState({ status: "signed-out", notice: SESSION_ENDED });
    });
    setPasswordRequiredHandler(() => {
      setState({ status: "loading" });
      setAttempt((a) => a + 1);
    });
    return () => {
      setUnauthorizedHandler(null);
      setPasswordRequiredHandler(null);
    };
  }, []);

  const signIn = useCallback((res: AuthResponse) => {
    setToken(res.token);
    setState(fromResponse(res.token, res.user, res.site));
  }, []);

  const signOut = useCallback(async (notice?: string) => {
    // Stop pushes to this browser for this account before dropping the token.
    await forgetPushDevice().catch(() => undefined);
    setToken(null);
    clearBootFlag();
    setState({ status: "signed-out", notice });
  }, []);

  const updateSite = useCallback((site: Site) => {
    setState((s) => (s.status === "signed-in" ? { ...s, site } : s));
  }, []);

  const updateUser = useCallback((user: User) => {
    setState((s) => (s.status === "signed-in" && s.user.id === user.id ? { ...s, user } : s));
  }, []);

  const retry = useCallback(() => {
    setState({ status: "loading" });
    setAttempt((a) => a + 1);
  }, []);

  const value = useMemo(
    () => ({ state, signIn, signOut, updateSite, updateUser, retry }),
    [state, signIn, signOut, updateSite, updateUser, retry],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthApi {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth outside AuthProvider");
  return v;
}

/** For screens behind the login gate. */
export function useSession(): { token: string; user: User; site: Site; isOwner: boolean } {
  const { state } = useAuth();
  if (state.status !== "signed-in") throw new Error("useSession used while signed out");
  return { token: state.token, user: state.user, site: state.site, isOwner: state.user.role === "owner" };
}
